import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { apiParameters } from 'src/app/configs/api.params';
import { distinctUntilChanged, map, take, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { IUser, IUserModel } from '../models/user.model';
import { StorageService } from './storage/storage.service';
import { UserService } from './user.service';
import { WhitelabelService } from './whitelabel.service';
import { defaultColors, themeColors } from '../models/colors';
const userKey = 'user';
const tokenKey = 'token';
const rememberMeKey = 'rememberMe';

@Injectable({ providedIn: 'root' })
export class AuthService {
  INIT_VALUE = null;

  private isAuthenticated$ = new BehaviorSubject<boolean>(this.INIT_VALUE);
  public isAuthenticated = this.isAuthenticated$.asObservable();

  private LoggedinUser$ = new BehaviorSubject<IUser>(this.INIT_VALUE);
  public LoggedinUser = this.LoggedinUser$.asObservable().pipe(
    distinctUntilChanged()
  );

  private userToken$ = new BehaviorSubject<string>(this.INIT_VALUE);
  public userToken = this.userToken$
    .asObservable()
    .pipe(distinctUntilChanged());

  private rememberMe$ = new BehaviorSubject<boolean>(false);
  private rememberMe = this.rememberMe$.value;

  private colors = themeColors;

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private userService: UserService,
    private colorService: WhitelabelService
  ) {}

  /**
   * Login method that calls API route
   * @param email - is user email that he entered
   * @param password - is user password that he entered
   * @param remember - is if user clicked on remember me.
   * @returns - token and user object
   */
  login(email: string, password: string, remember: boolean) {
    return this.http
      .post(`${apiParameters.url}/login`, { email, password })
      .pipe(
        map((res: any) => {
          if (res.user.company && res.user.company.whitelabel) {
            res.user.company.whitelabel.colors = Object.keys(
              res.user.company.whitelabel
            ).reduce((arr, key) => {
              let color = {
                name: key,
                value: res.user.company.whitelabel[key],
              };
              return arr.concat(color);
            }, []);

            res.user.company.whitelabel.colors =
              res.user.company.whitelabel.colors.filter((item) =>
                this.colors.includes(item.name)
              );
          }

          return res;
        }),
        tap((res: any) => {
          const token = res.token;
          const user = res.user;

          if (token) {
            const storage = remember ? localStorage : sessionStorage;

            this.storage.saveUser(storage, JSON.stringify({ user, token }));
            this.storage.saveToken(storage, token);

            if (remember) {
              this.storage.setLocalObject(
                rememberMeKey,
                JSON.stringify(remember)
              );
              this.rememberMe$.next(true);
            }

            if (user.company && user.company.whitelabel) {
              this.colorService.setColors(user.company.whitelabel.colors);
            }

            if (
              user.company &&
              user.company.whitelabel &&
              user.company.whitelabel.logoUrl
            ) {
              this.colorService.setLogo(user.company.whitelabel.logoUrl);
            }

            this.LoggedinUser$.next(user);
            this.userToken$.next(token);
            this.isAuthenticated$.next(true);
          }
        })
      );
  }

  logout() {
    this.storage.removeSessionObject(userKey);
    this.storage.removeSessionObject(tokenKey);
    this.storage.removeLocalObject(userKey);
    this.storage.removeLocalObject(tokenKey);
    this.storage.removeLocalObject(rememberMeKey);

    this.isAuthenticated$.next(false);
    this.LoggedinUser$.next(null);
    this.userToken$.next(null);
    this.rememberMe$.next(false);

    this.colorService.setColors(defaultColors);
  }

  /**
   * Auto Login user - Check if user is in local storage
   * if yes, get user data from backend and athenticate
   * @returns
   */
  autoLogin() {
    let rememberMe;
    if (this.storage.getLocalObject(rememberMeKey)) {
      this.rememberMe$.next(this.storage.getLocalObject(rememberMeKey));
      rememberMe = this.rememberMe$.value;
    }

    const userData: IUserModel = rememberMe
      ? this.storage.getLocalObject(userKey)
      : this.storage.getSessionObject(userKey);

    if (!userData) {
      console.log(userData)
      this.isAuthenticated$.next(false);
      return;
    }
    console.log(userData)

    this.userToken$.next(userData.token);
    if(userData.user.role?.name == 'filialAdmin') {
      this.LoggedinUser$.next(userData.user);
      this.isAuthenticated$.next(true);
    } else {
    // this.LoggedinUser$.next(userData.user);
    // this.isAuthenticated$.next(true);

    return this.userService
      .getById(userData.user.id)
      .pipe(
        take(1),
        map((user: any) => {
          if (user.company && user.company.whitelabel) {
            user.company.whitelabel.colors = Object.keys(
              user.company.whitelabel
            ).reduce((arr, key) => {
              let color = { name: key, value: user.company.whitelabel[key] };
              return arr.concat(color);
            }, []);

            user.company.whitelabel.colors =
              user.company.whitelabel.colors.filter((item) =>
                this.colors.includes(item.name)
              );
          }

          return user;
        })
      )
      .subscribe(
        (user) => {
          if (user.company && user.company.whitelabel) {
            this.colorService.setColors(user.company.whitelabel.colors);
          }

          if (
            user.company &&
            user.company.whitelabel &&
            user.company.whitelabel.logoUrl
          ) {
            this.colorService.setLogo(user.company.whitelabel.logoUrl);
          }

          this.LoggedinUser$.next(user);
          this.isAuthenticated$.next(true);
          // sessionStorage.setItem('currentUser', JSON.stringify({ user, token }));
        },
        (err) => {
          console.log(err);
          this.logout();
          // this.router.navigate(['/login']);
        }
      );
      }
    // const loadedUser = new User(
    //   userData.email,
    //   userData.id,
    //   userData._token,
    //   new Date(userData._tokenExpirationDate)
    // );

    // if (loadedUser.token) {
    //   this.user.next(loadedUser);
    //   const expirationDuration =
    //     new Date(userData._tokenExpirationDate).getTime() -
    //     new Date().getTime();
    //   this.autoLogout(expirationDuration);
    // }
  }
}
